import { Grid } from '@material-ui/core';
import { Button, CurrencyField, DateField, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { delinquencyService } from '../delinquencyService';

export type PropertyAppraisalsFormData = {
  address: string;
  asIsValue: number | string;
  appraisalDate: Date | null;
};

export type PropertyAppraisalsFormProps = {
  handleClose: () => void;
  onSave?: (data: PropertyAppraisalsFormData) => void | Promise<void>;
  properties?: any;
};

export const PropertyAppraisalsForm = observer(
  ({ handleClose, onSave, properties }: PropertyAppraisalsFormProps) => {
    const [localProperties, setLocalProperties] = useState<Record<string, unknown>[]>(
      () => (Array.isArray(properties) ? [...properties] : [])
    );

    const updateState = (name: string, value: string | number, index: number) => {
      setLocalProperties((prev) => {
        if (prev.length === 0 && index === 0) {
          return [{ [name]: value }];
        }
        return prev.map((item, i) =>
          i === index ? { ...item, [name]: value } : item
        );
      });
    };

    const toDateOrNull = (value: unknown): Date | null => {
      if (value == null || value === '') return null;
      if (value instanceof Date) return value;
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const handleSave = async () => {
      await delinquencyService.updatePostApprovalPropertyAppraisals(localProperties);
      if (onSave) {
        const first = localProperties?.[0] as Record<string, unknown> | undefined;
        await onSave({
          address: String(first?.address ?? ''),
          asIsValue: (first?.asIsValue as string | number | undefined) ?? '',
          appraisalDate: toDateOrNull(first?.appraisalDate),
        });
      }
      handleClose();
    };

    return (
      <Grid container spacing={2}>
        {localProperties.map((property, index) => {
          const p = property as Record<string, unknown>;
          const address = String(p.address ?? '');
          const asIsValue = (p.asIsValue as string | number | undefined) ?? '';
          const appraisalDate = toDateOrNull(p.appraisalDate);

          return (
            <Grid item xs={12} key={`property-appraisal-${index}`}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    label="Address"
                    value={address}
                    fullWidth
                    disabled={true}
                    margin="normal"
                    testId={`property-appraisals-address-${index}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CurrencyField
                    label="As Is Value"
                    name="asIsValue"
                    value={asIsValue}
                    onChange={(_e, value) => updateState('asIsValue', value, index)}
                    fullWidth
                    variant="outlined"
                    testId={`property-appraisals-as-is-value-${index}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DateField
                    label="Appraisal Date"
                    value={appraisalDate}
                    onChange={(value) =>
                      updateState('appraisalDate', value as unknown as string | number, index)
                    }
                    format="MM/dd/yyyy"
                    fullWidth
                    inputVariant="outlined"
                    testId={`property-appraisals-date-${index}`}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}

        <Grid item xs={12}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button
                type="button"
                variant="contained"
                color="default"
                testId="property-appraisals-close"
                onClick={handleClose}
              >
                Close
              </Button>
            </Grid>
            <Grid item>
              <Button
                type="button"
                variant="contained"
                color="primary"
                testId="property-appraisals-save"
                onClick={handleSave}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);
